import React, { useMemo, useState, useEffect } from "react";
import NavigationBar from "./NavigationBar";
import { Button } from "./ui/button";
import { Trash2, Pencil, AlertTriangle } from "lucide-react";
import BiometricEnrollment from "./BiometricEnrollment";
import { apiFetch } from "../lib/api";
import { useDemoMode } from "../contexts/DemoModeContext";  // match the relative path

/** ===========================
 *  Pilot Switch
 *  - true  => photo-only save (no biometric gate)
 *  - false => require biometric enrollment for resident/staff
 *  =========================== */
const PILOT = false;

/* -------------------- Types -------------------- */
type Resident = {
    id: number;
    role: string;
    name: string;
    room_number: string;
    phone_number: string;
    enable_text_alerts: boolean;
    elopement_risk: boolean;
    access_risk: boolean;
    is_inside?: boolean;
    photoUrl?: string;
    visiting_with_id?: number | null;
    visiting_with_name?: string | null;
    visiting_with_room?: string | null;
    has_face_encoding?: boolean;
    safety_alert_level_2?: boolean;
};

type PhoneEntry = { number: string };

function normalizeUSPhone(raw: string): string | null {
  const digits = (raw || "").replace(/\D/g, "");
  let core;
  if (digits.length === 11 && digits[0] === "1") core = digits.slice(1);
  else if (digits.length === 10) core = digits;
  else return null;
  // US area code and exchange can't start with 0 or 1
  if ("01".includes(core[0]) || "01".includes(core[3])) return null;
  return `+1${core}`;
}

function formatUSPhone(e164: string): string {
  const d = e164.replace(/\D/g, "").slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : e164;
}


type StaffPhoneOption = { key: string; label: string; number: string };

/* -------------------- Phone number list -------------------- */
// Shared by the add form and the edit modal. When staffOptions is given
// (residents only), each row gets a dropdown: picking a staff member fills
// in their number, "Other number" allows free entry. The dropdown value is
// derived from the row's number, so typing a staff number shows their name
// and editing it by hand switches the row back to "Other number".
function PhoneNumberList({
  numbers,
  onChange,
  staffOptions,
}: {
  numbers: PhoneEntry[];
  onChange: (next: PhoneEntry[]) => void;
  staffOptions?: StaffPhoneOption[];
}) {
  const showDropdown = !!staffOptions && staffOptions.length > 0;

  return (
    <div className="space-y-2">
      {numbers.map((num, i) => {
        const normalized = normalizeUSPhone(num.number);
        const match = showDropdown
          ? staffOptions!.find((o) => o.number === normalized)
          : undefined;

        const update = (value: string) => {
          const next = [...numbers];
          next[i] = { number: value };
          onChange(next);
        };

        return (
          <div key={i} className="flex items-center gap-2">
            {showDropdown && (
              <select
                value={match ? match.key : "custom"}
                onChange={(e) => {
                  const opt = staffOptions!.find((o) => o.key === e.target.value);
                  update(opt ? opt.number : "");
                }}
                className="w-40 shrink-0 px-2 py-2 rounded-md bg-gray-800 text-white border border-gray-700 text-sm focus:outline-none focus:border-blue-500"
                title="Choose a staff member or enter another number"
              >
                <option value="custom">Other number</option>
                {staffOptions!.map((o) => (
                  <option key={o.key} value={o.key}>
                    {o.label}
                  </option>
                ))}
              </select>
            )}
            <input
              type="tel"
              placeholder={`Phone Number ${i + 1}`}
              value={num.number}
              onChange={(e) => update(e.target.value)}
              className="flex-1 min-w-0 px-3 py-2 rounded-md bg-gray-800 text-white border border-gray-700 focus:outline-none focus:border-blue-500"
            />
            {numbers.length > 1 && (
              <button
                type="button"
                onClick={() => onChange(numbers.filter((_, idx) => idx !== i))}
                className="text-red-400 hover:text-red-500 transition"
                title="Remove this number"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            )}
          </div>
        );
      })}
      <button
        type="button"
        onClick={() => onChange([...numbers, { number: "" }])}
        className="text-sm text-emerald-400 hover:text-emerald-300 transition"
      >
        + Add another number
      </button>
    </div>
  );
}

export default function RosterDashboard() {
  const [roster, setRoster] = useState<Resident[]>([]);
  const [search, setSearch] = useState("");
  const [userType, setUserType] = useState<"staff" | "resident" | "visitor">("resident");
  const [name, setName] = useState("");
  const [roomNumber, setRoomNumber] = useState("");
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneEntry[]>([{ number: "" }]);
  const [photo, setPhoto] = useState<File | null>(null);
  const [enableTextAlerts, setEnableTextAlerts] = useState(false);
  const [elopementRisk, setElopementRisk] = useState(false);
  const [accessRisk, setAccessRisk] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedResident, setSelectedResident] = useState<number | "">("");
  const { displayName } = useDemoMode();

  const [roleFilter, setRoleFilter] = useState<"all" | "resident" | "staff" | "visitor">("all");
  const [showBiometricEnrollment, setShowBiometricEnrollment] = useState(false);

  // Edit modal state
  const [editingPerson, setEditingPerson] = useState<Resident | null>(null);
  const [editName, setEditName] = useState("");
  const [editRoomNumber, setEditRoomNumber] = useState("");
  const [editElopementRisk, setEditElopementRisk] = useState(false);
  const [editAccessRisk, setEditAccessRisk] = useState(false);
  const [editPhoto, setEditPhoto] = useState<File | null>(null);
  const [editEnrollmentId, setEditEnrollmentId] = useState<string | null>(null);
  const [showEditBiometric, setShowEditBiometric] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editEnableTextAlerts, setEditEnableTextAlerts] = useState(false);
  const [editPhoneNumbers, setEditPhoneNumbers] = useState<PhoneEntry[]>([{ number: "" }]);
  const [editIsInside, setEditIsInside] = useState(false);
  const [isClearingEmbeddings, setIsClearingEmbeddings] = useState(false);
  const [safetyLevel2, setSafetyLevel2] = useState(false);
  const [editSafetyLevel2, setEditSafetyLevel2] = useState(false);


  // legacy key kept only for child prop compatibility (modal ignores it now)
  const storageKey = useMemo(() => `enroll::new::${Date.now()}`, []);

  // Only used when PILOT === false
  const [enrollmentId, setEnrollmentId] = useState<string | null>(null);
  useEffect(() => {
    if (PILOT) return;
    function onReady(e: any) {
      if (e?.detail?.enrollment_id) setEnrollmentId(String(e.detail.enrollment_id));
    }
    window.addEventListener("biometric-ready", onReady);
    return () => window.removeEventListener("biometric-ready", onReady);
  }, []);

  useEffect(() => {
    if (PILOT) return;

    async function onReady(e: any) {
        if (!e?.detail?.enrollment_id) return;

        // Set editEnrollmentId in case update_resident_full path is used
        setEditEnrollmentId(String(e.detail.enrollment_id));

        // Refresh roster so has_face_encoding updates immediately
        try {
            const refreshed = await apiFetch(
                `/get_residents?t=${Date.now()}`,
                { cache: "no-store" }
            ).then(r => r.json());
            setRoster(refreshed);

            // Update editingPerson so the modal reflects new enrollment state
            if (editingPerson) {
                const updated = refreshed.find((r: Resident) => r.id === editingPerson.id);
                if (updated) setEditingPerson(updated);
            }
        } catch (err) {
            console.error("Failed to refresh roster after enrollment:", err);
        }
    }

    window.addEventListener("biometric-ready", onReady);
    return () => window.removeEventListener("biometric-ready", onReady);
}, [editingPerson]); // editingPerson in deps so closure has current value


  const getLastName = (full: string) => {
    if (!full) return "";
    const parts = full.trim().split(/\s+/);
    return parts[parts.length - 1].toLowerCase();
  };
  const getFirstNames = (full: string) => {
    if (!full) return "";
    const parts = full.trim().split(/\s+/);
    return parts.slice(0, -1).join(" ").toLowerCase();
  };

  // Staff phone numbers offered in the residents' alert dropdown.
  // A staff member with several numbers gets one option per number.
  const staffPhoneOptions = useMemo(() => {
    const opts: StaffPhoneOption[] = [];
    for (const s of roster) {
      if ((s.role || "").toLowerCase() !== "staff" || !s.phone_number) continue;
      for (const raw of s.phone_number.split(",")) {
        const number = normalizeUSPhone(raw.trim());
        if (!number) continue;
        opts.push({
          key: `${s.id}:${number}`,
          label: `${displayName(s.id, s.name)} — ${formatUSPhone(number)}`,
          number,
        });
      }
    }
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [roster, displayName]);

  /* -------------------- Data -------------------- */
  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch("/get_residents");
        setRoster(await res.json());
      } catch (e) {
        console.error("Error fetching roster:", e);
      }
    })();
  }, []);

  /* -------------------- Handlers -------------------- */
  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) setPhoto(e.target.files[0]);
  };

  const handleAdd = async () => {
    if (isSubmitting) return;

    if (!photo) {
      alert("⚠️ Photo required! Please upload a photo before saving.");
      return;
    }

    // Biometric enrollment is now OPTIONAL for residents/staff.
    // It can be completed later via the edit flow.

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("role", userType);
      formData.append("name", name.trim());

      if (userType === "resident") {
        formData.append("room_number", roomNumber.trim());
        formData.append("elopement_risk", String(!!elopementRisk));
      }

      if (userType === "visitor") {
        formData.append("access_risk", String(!!accessRisk));

        if (selectedResident !== "") {
          formData.append("visiting_with_id", selectedResident.toString());
        }
      }

      // Text alerts — all roles
      if (enableTextAlerts) {
        const normalized: string[] = [];
        for (const p of phoneNumbers) {
          const raw = p.number.trim();
          if (!raw) continue;
          const clean = normalizeUSPhone(raw);
          if (!clean) {
            alert(`"${raw}" isn't a valid US phone number. Enter 10 digits.`);
            return; // stops the save
          }
          if (!normalized.includes(clean)) normalized.push(clean);
        }
        if (normalized.length === 0) {
          alert("Text alerts are on but no valid phone number was entered.");
          return;
        }
        formData.append("enable_text_alerts", "true");
        formData.append("phone_number", normalized.join(","));
      } else {
        formData.append("enable_text_alerts", "false");
      }
      formData.append("safety_alert_level_2", String(!!safetyLevel2));

      formData.append("photo", photo, photo.name || "photo.jpg");

      // Only attach enrollment_id if one exists (user completed enrollment).
      // Residents/staff saved without it can be enrolled later via edit.
      if (!PILOT && enrollmentId && (userType === "resident" || userType === "staff")) {
        formData.append("enrollment_id", enrollmentId);
      }

      const res = await apiFetch("/add_resident", { method: "POST", body: formData });
      const data = await res.json();

      if (!res.ok) {
        if (data?.error === "Duplicate centroid detected") {
          if (!PILOT) setEnrollmentId(null);
          alert("Duplicate centroid detected. Please re-enroll biometrics for this person.");
        } else {
          alert(data?.error || data?.detail || "Failed to add person. Please try again.");
        }
        return;
      }

      setName("");
      setRoomNumber("");
      setPhoneNumbers([{ number: "" }]);
      setPhoto(null);
      setEnableTextAlerts(false);
      setElopementRisk(false);
      setAccessRisk(false);
      setSafetyLevel2(false);
      setSelectedResident("");
      if (!PILOT) setEnrollmentId(null);

      const refreshed = await apiFetch(`/get_residents?t=${Date.now()}`, { cache: "no-store" }).then(r => r.json());
      setRoster(refreshed);

      // Let the user know if biometrics are pending
      const needsBiometrics =
        !PILOT && !enrollmentId && (userType === "resident" || userType === "staff");

      if (needsBiometrics) {
        alert("✅ Person added successfully!\n\nℹ️ Biometric enrollment is still needed. You can complete it later by editing this person.");
      } else {
        alert("✅ Person added successfully!");
      }
    } catch (err) {
      console.error("Error saving person:", err);
      alert("An unexpected error occurred. Please check console logs.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClearEmbeddings = async () => {
    if (!editingPerson) return;
    if (!window.confirm(
        `Delete all embeddings for ${editingPerson.name}? This cannot be undone.`
    )) return;

    setIsClearingEmbeddings(true);
    try {
        const res = await apiFetch(
            `/api/residents/${editingPerson.id}/embeddings`,
            { method: "DELETE" }
        );
        const data = await res.json();
        if (!res.ok) {
            alert(data?.detail || "Failed to clear embeddings.");
            return;
        }

        alert(`✅ Cleared ${data.deleted} embeddings for ${editingPerson.name}.`);

        // Refresh roster so has_face_encoding updates
        const refreshed = await apiFetch(
            `/get_residents?t=${Date.now()}`,
            { cache: "no-store" }
        ).then(r => r.json());
        setRoster(refreshed);

        const updated = refreshed.find((r: Resident) => r.id === editingPerson.id);
        if (updated) setEditingPerson(updated);

    } catch (err) {
        console.error("Failed to clear embeddings:", err);
        alert("An unexpected error occurred.");
    } finally {
        setIsClearingEmbeddings(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this person?")) return;
    const prev = roster;
    setRoster(prev.filter(r => r.id !== id));
    try {
      const resp = await apiFetch(`/delete_resident/${id}`, { method: "DELETE" });
      if (!resp.ok) {
        setRoster(prev);
        const text = await resp.text();
        alert(`Delete failed: ${resp.status} ${text}`);
        return;
      }
      const refreshed = await apiFetch(`/get_residents?t=${Date.now()}`, { cache: "no-store" }).then(r => r.json());
      setRoster(refreshed);
    } catch (e) {
      setRoster(prev);
      console.error("Delete error:", e);
      alert("Delete failed due to a network or server error.");
    }
  };

  const handleEdit = (person: Resident) => {
    setEditingPerson(person);
    setEditName(person.name);
    setEditRoomNumber(person.room_number || "");
    setEditElopementRisk(person.elopement_risk || false);
    setEditAccessRisk(person.access_risk || false);
    setEditPhoto(null);
    setEditEnrollmentId(null);
    setShowEditBiometric(false);
    setEditEnableTextAlerts(person.enable_text_alerts || false);
    setEditPhoneNumbers(
        person.phone_number
            ? person.phone_number.split(",").map(n => ({ number: n.trim() }))
            : [{ number: "" }]
    );
    setEditIsInside(person.is_inside || false);
    setEditSafetyLevel2(person.safety_alert_level_2 || false);
  };

  const handleUpdate = async () => {
    if (!editingPerson || isUpdating) return;

    setIsUpdating(true);
    try {
      const formData = new FormData();
      const role = (editingPerson.role || "").toLowerCase();

      if (editName.trim() !== editingPerson.name) {
        formData.append("name", editName.trim());
      }

      if (role === "resident") {
        if (editRoomNumber.trim() !== (editingPerson.room_number || "")) {
          formData.append("room_number", editRoomNumber.trim());
        }
        if (editElopementRisk !== editingPerson.elopement_risk) {
          formData.append("elopement_risk", String(editElopementRisk));
        }
        if (editSafetyLevel2 !== editingPerson.safety_alert_level_2) {
          formData.append("safety_alert_level_2", String(editSafetyLevel2));
        }
      }

      if (role === "visitor") {
        if (editAccessRisk !== editingPerson.access_risk) {
          formData.append("access_risk", String(editAccessRisk));
        }
      }

      // === TEXT ALERTS — residents, visitors, and staff ===
      if (role === "resident" || role === "visitor" || role === "staff") {
        if (editEnableTextAlerts) {
          const normalized: string[] = [];
          for (const p of editPhoneNumbers) {
            const raw = p.number.trim();
            if (!raw) continue;
            const clean = normalizeUSPhone(raw);
            if (!clean) {
              alert(`"${raw}" isn't a valid US phone number. Enter 10 digits.`);
              setIsUpdating(false);
              return; // stops the save
            }
            if (!normalized.includes(clean)) normalized.push(clean);
          }
          if (normalized.length === 0) {
            alert("Text alerts are on but no valid phone number was entered.");
            setIsUpdating(false);
            return;
          }
          const joined = normalized.join(",");
          if (editEnableTextAlerts !== editingPerson.enable_text_alerts ||
              joined !== (editingPerson.phone_number || "")) {
            formData.append("enable_text_alerts", "true");
            formData.append("phone_number", joined);
          }
        } else {
          if (editEnableTextAlerts !== editingPerson.enable_text_alerts) {
            formData.append("enable_text_alerts", "false");
            formData.append("phone_number", "");
          }
        }
      }

      if (editPhoto) {
        formData.append("photo", editPhoto, editPhoto.name || "photo.jpg");
      }

      if (editEnrollmentId && (role === "resident" || role === "staff")) {
        formData.append("enrollment_id", editEnrollmentId);
      }

      // Nothing to send?
      if (![...formData.keys()].length) {
        setEditingPerson(null);
        return;
      }

      const res = await apiFetch(`/update_resident/${editingPerson.id}/full`, {
        method: "PATCH",
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data?.error || "Failed to update. Please try again.");
        return;
      }

      const refreshed = await apiFetch(`/get_residents?t=${Date.now()}`, { cache: "no-store" }).then(r => r.json());
      setRoster(refreshed);

      setEditingPerson(null);
    } catch (err) {
      console.error("Error updating person:", err);
      alert("An unexpected error occurred. Please check console logs.");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleIsInsideToggle = async (value: boolean) => {
    if (!editingPerson) return;
    setEditIsInside(value);

    try {
        const res = await apiFetch(`/update_resident/${editingPerson.id}/is_inside`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ is_inside: value }),
        });

        if (!res.ok) {
            const data = await res.json();
            alert(data?.error || "Failed to update status.");
            setEditIsInside(!value); // revert on failure
            return;
        }

        // Update editingPerson and roster to reflect change
        const updated = { ...editingPerson, is_inside: value };
        setEditingPerson(updated);
        setRoster(prev => prev.map(r => r.id === editingPerson.id ? updated : r));

    } catch (err) {
        console.error("Failed to update is_inside:", err);
        setEditIsInside(!value); // revert on failure
    }
  };

  const filteredRoster = React.useMemo(() => {
    const roleOK = (r: Resident) => roleFilter === "all" ? true : (r.role || "").toLowerCase() === roleFilter;
    const searchOK = (r: Resident) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return (displayName(r.id, r.name) || "").toLowerCase().includes(q) || (r.room_number || "").toLowerCase().includes(q);
    };
    const arr = roster.filter((r) => roleOK(r) && searchOK(r));
    arr.sort((a, b) => {
      const lnA = getLastName(a.name), lnB = getLastName(b.name);
      if (lnA !== lnB) return lnA.localeCompare(lnB);
      const fnA = getFirstNames(a.name), fnB = getFirstNames(b.name);
      return fnA.localeCompare(fnB);
    });
    return arr;
  }, [roster, roleFilter, search]);

  /* -------------------- UI -------------------- */
  return (
    <div className="bg-black min-h-screen text-white flex flex-col">
      <NavigationBar />

      <div className="flex flex-1 min-h-0">
        {/* Left: roster list */}
        <div className="w-1/2 border-r border-gray-800 p-4 overflow-y-auto min-h-0">
          <div className="sticky top-0 bg-black pb-4">
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 px-3 py-2 rounded-md bg-gray-800 text-white"
              />
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value as typeof roleFilter)}
                className="px-3 py-2 rounded-md bg-gray-800 text-white border border-gray-700"
                title="Filter by role"
              >
                <option value="all">All</option>
                <option value="resident">Resident</option>
                <option value="staff">Staff</option>
                <option value="visitor">Visitor</option>
              </select>
            </div>
          </div>

          <ul className="space-y-4">
            {filteredRoster.map((p) => {
              const role = (p.role || "").toLowerCase();
              const showElopement = role === "resident" && p.elopement_risk;
              const showAccess = role === "visitor" && p.access_risk;
              return (
                <li key={p.id} className="flex items-center justify-between gap-3 border-b border-gray-800 pb-2">
                  <div className="flex items-center gap-4">
                    <img
                      src={p.photoUrl ? `https://api.monivue.io${p.photoUrl}` : "/placeholder-avatar.png"}
                      className="w-12 h-12 rounded-full object-cover bg-gray-700"
                    />
                    <div>
                      <p className="font-semibold text-white flex items-center gap-2">
                        {displayName(p.id, p.name)}
                        {showElopement && (
                          <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs border-rose-500/40 bg-rose-600/20 text-rose-300">
                            <AlertTriangle className="w-3 h-3" /> Safety Alert
                          </span>
                        )}

                        {showAccess && (
                          <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs border-amber-500/40 bg-amber-500/20 text-amber-200">
                            <AlertTriangle className="w-3 h-3" /> Access Risk
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-gray-400">
                        {p.role.toLowerCase() === "resident"
                          ? `Room ${p.room_number}`
                          : p.role.toLowerCase() === "staff"
                          ? "Staff"
                          : p.visiting_with_name
                          ? `Visiting ${displayName(p.visiting_with_id, p.visiting_with_name)} — Room ${p.visiting_with_room ?? "—"}`
                          : "Visitor"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(p)}
                      className="text-gray-400 hover:text-blue-400 transition"
                      title="Edit"
                    >
                      <Pencil className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(p.id)}
                      className="text-gray-400 hover:text-red-500 transition"
                      title="Delete"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Right: add form */}
        <div className="w-1/2 p-6 overflow-y-auto">
          <h2 className="text-xl font-bold mb-6">Add New Person</h2>

          {/* Role */}
          <div className="mb-4">
            <label className="block mb-2">Role</label>
            <div className="flex gap-6">
              {(["staff", "resident", "visitor"] as const).map((type) => (
                <label key={type} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="userType"
                    value={type}
                    checked={userType === type}
                    onChange={() => setUserType(type)}
                  />
                  {type[0].toUpperCase() + type.slice(1)}
                </label>
              ))}
            </div>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleAdd();
            }}
          >
            {/* Name */}
            <input
              type="text"
              placeholder="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 mb-4 rounded-md bg-gray-800 text-white"
            />

            {/* Room for residents */}
            {userType === "resident" && (
              <input
                type="text"
                placeholder="Room Number"
                value={roomNumber}
                onChange={(e) => setRoomNumber(e.target.value)}
                className="w-full px-3 py-2 mb-4 rounded-md bg-gray-800 text-white"
              />
            )}

            {/* Visitor: who they are visiting */}
            {userType === "visitor" && (
              <div className="mb-4">
                <label className="block mb-2 font-medium">Visiting With</label>
                <select
                  value={selectedResident === "" ? "" : String(selectedResident)}
                  onChange={(e) => {
                    const v = e.target.value;
                    setSelectedResident(v ? Number(v) : "");
                  }}
                  className="w-full px-3 py-2 rounded-md bg-gray-800 text-white"
                >
                  <option value="">Select a resident</option>
                  {roster
                    .filter((person) => (person.role || "").toLowerCase() === "resident")
                    .map((resident) => (
                      <option key={resident.id} value={resident.id}>
                        {displayName(resident.id, resident.name)} — Room {resident.room_number}
                      </option>
                    ))}
                </select>
              </div>
            )}

            {/* Photo upload */}
            <div className="mb-4">
              <label className="block mb-2">
                Photo Upload {PILOT}
              </label>
              <input type="file" onChange={handlePhotoUpload} />
            </div>

            {/* Biometric Enrollment (hidden in pilot mode) */}
            {!PILOT && (userType === "resident" || userType === "staff") && (
              <div className="mb-6">
                <button
                  type="button"
                  onClick={() => {
                    if (!name.trim()) {
                      alert("Please enter the resident's name first.");
                      return;
                    }
                    setShowBiometricEnrollment(true);
                  }}
                  className="w-full bg-indigo-600 hover:bg-indigo-500 text-white py-2 rounded-md transition"
                >
                  Launch Biometric Enrollment
                </button>
                {enrollmentId && (
                  <p className="mt-2 text-xs text-emerald-300">
                    Biometric ready (eid: {enrollmentId.slice(0, 8)}…)
                  </p>
                )}
              </div>
            )}

            {/* Enable Text Alerts — residents, visitors, and staff */}
            <div className="mb-4 space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={enableTextAlerts}
                  onChange={(e) => setEnableTextAlerts(e.target.checked)}
                />
                Enable Text Alerts
              </label>
              <p className="text-xs text-gray-400 ml-6">
                By enabling text alerts, the recipient will receive automated safety alerts from MoniVue.
                Message frequency varies. Msg & data rates may apply. Reply STOP to opt out or HELP for assistance.
                Terms: monivue.io/terms-conditions | Privacy: monivue.io/privacy-policy
              </p>

              {enableTextAlerts && (
                <PhoneNumberList
                  numbers={phoneNumbers}
                  onChange={setPhoneNumbers}
                  staffOptions={userType === "resident" ? staffPhoneOptions : undefined}
                />
              )}
            </div>

            {/* Risk flags */}
            <div className="mb-4 space-y-2">
              {userType === "resident" && (
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={elopementRisk}
                    onChange={(e) => setElopementRisk(e.target.checked)}
                  />
                  Safety Alert
                </label>
              )}
              {userType === "resident" && (
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={safetyLevel2}
                    onChange={(e) => setSafetyLevel2(e.target.checked)}
                  />
                  Safety Alert Level II (recurring)
                </label>
              )}
              {userType === "visitor" && (
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={accessRisk}
                    onChange={(e) => setAccessRisk(e.target.checked)}
                  />
                  Access Risk
                </label>
              )}
            </div>

            <Button
              type="submit"
              disabled={!name || !photo || isSubmitting}
              className="w-full bg-emerald-600 hover:bg-emerald-500"
            >
              {isSubmitting ? "Saving..." : "Save"}
            </Button>

            {!PILOT && showBiometricEnrollment && (
              <BiometricEnrollment
                name={name}
                storageKey={storageKey}
                onClose={() => setShowBiometricEnrollment(false)}
              />
            )}
          </form>
        </div>
      </div>

      {/* Edit Modal */}
      {editingPerson && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/70" onClick={() => setEditingPerson(null)} />
          <div className="relative w-[92vw] max-w-md max-h-[90vh] flex flex-col rounded-2xl bg-[#0d1117] border border-white/10 shadow-2xl">

            {/* Scrollable body */}
            <div className="overflow-y-auto p-6">
              <h3 className="text-xl font-bold mb-4">Edit Profile</h3>

              <div className="flex items-center gap-4 mb-6">
                <img
                  src={editingPerson.photoUrl ? `https://api.monivue.io${editingPerson.photoUrl}` : "/placeholder-avatar.png"}
                  className="w-16 h-16 rounded-full object-cover bg-gray-700"
                />
                <div>
                  <p className="text-gray-400 text-sm capitalize">{editingPerson.role}</p>
                </div>
              </div>

              {/* Name */}
              <div className="mb-4">
                <label className="block text-sm text-gray-300 mb-1">Name</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-3 py-2 rounded-md bg-gray-800 text-white border border-gray-700 focus:outline-none focus:border-blue-500"
                />
              </div>

              {/* Room Number (residents only) */}
              {(editingPerson.role || "").toLowerCase() === "resident" && (
                <div className="mb-4">
                  <label className="block text-sm text-gray-300 mb-1">Room Number</label>
                  <input
                    type="text"
                    value={editRoomNumber}
                    onChange={(e) => setEditRoomNumber(e.target.value)}
                    className="w-full px-3 py-2 rounded-md bg-gray-800 text-white border border-gray-700 focus:outline-none focus:border-blue-500"
                  />
                </div>
              )}

              {/* Safety Alert (residents only) */}
              {(editingPerson.role || "").toLowerCase() === "resident" && (
                <div className="mb-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editElopementRisk}
                      onChange={(e) => setEditElopementRisk(e.target.checked)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-gray-300">Safety Alert</span>
                  </label>
                </div>
              )}
              {(editingPerson.role || "").toLowerCase() === "resident" && (
                <div className="mb-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editSafetyLevel2}
                      onChange={(e) => setEditSafetyLevel2(e.target.checked)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-gray-300">Safety Alert Level II (recurring)</span>
                  </label>
                </div>
              )}

              {/* Access Risk (visitors only) */}
              {(editingPerson.role || "").toLowerCase() === "visitor" && (
                <div className="mb-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editAccessRisk}
                      onChange={(e) => setEditAccessRisk(e.target.checked)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm text-gray-300">Access Risk</span>
                  </label>
                </div>
              )}

              {/* Photo replacement */}
              <div className="mb-4">
                <label className="block text-sm text-gray-300 mb-1">Replace Photo (optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setEditPhoto(e.target.files?.[0] || null)}
                  className="text-sm text-gray-300"
                />
                {editPhoto && (
                  <p className="mt-1 text-xs text-emerald-300">New photo selected: {editPhoto.name}</p>
                )}
              </div>

              {/* Text Alerts — residents, visitors, and staff */}
              <div className="mb-4 space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={editEnableTextAlerts}
                    onChange={(e) => setEditEnableTextAlerts(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-gray-300">Enable Text Alerts</span>
                </label>

                {editEnableTextAlerts && (
                  <div className="mt-2">
                    <PhoneNumberList
                      numbers={editPhoneNumbers}
                      onChange={setEditPhoneNumbers}
                      staffOptions={
                        (editingPerson.role || "").toLowerCase() === "resident"
                          ? staffPhoneOptions
                          : undefined
                      }
                    />
                  </div>
                )}
              </div>

              {/* Biometric enrollment — only for resident/staff without existing encoding */}
              {!PILOT &&
                !editingPerson.has_face_encoding &&
                ((editingPerson.role || "").toLowerCase() === "resident" ||
                  (editingPerson.role || "").toLowerCase() === "staff") && (
                  <div className="mb-4 p-3 rounded-md bg-indigo-950/40 border border-indigo-800/40">
                    <p className="text-sm text-gray-200 mb-2">
                      <span className="font-medium">Biometric enrollment:</span>{" "}
                      {editEnrollmentId ? (
                        <span className="text-emerald-300">
                          Ready (eid: {editEnrollmentId.slice(0, 8)}…)
                        </span>
                      ) : (
                        <span className="text-gray-400">Not yet enrolled</span>
                      )}
                    </p>
                    {!editEnrollmentId && (
                      <button
                        type="button"
                        onClick={() => {
                          if (!editName.trim()) {
                            alert("Please enter a name before starting enrollment.");
                            return;
                          }
                          setShowEditBiometric(true);
                        }}
                        className="w-full bg-indigo-600 hover:bg-indigo-500 text-white py-2 rounded-md transition text-sm"
                      >
                        Start Biometric Enrollment
                      </button>
                    )}
                  </div>
                )}

              {/* Already enrolled notice */}
              {!PILOT &&
                editingPerson.has_face_encoding &&
                ((editingPerson.role || "").toLowerCase() === "resident" ||
                  (editingPerson.role || "").toLowerCase() === "staff") && (
                  <div className="mb-4 p-3 rounded-md bg-gray-800/50 border border-gray-700">
                    <p className="text-xs text-gray-400">
                      This person is already enrolled. Re-enrollment is not currently supported.
                    </p>
                  </div>
                )}

              {/* Clear Embeddings */}
              <div className="mb-4">
                  <button
                      type="button"
                      onClick={handleClearEmbeddings}
                      disabled={isClearingEmbeddings}
                      className="w-full bg-red-900/40 hover:bg-red-800/60 border border-red-700/40
                                 text-red-300 hover:text-red-200 py-2 rounded-md transition text-sm"
                  >
                      {isClearingEmbeddings ? "Clearing..." : "🗑 Clear Embeddings"}
                  </button>
                  <p className="mt-1 text-xs text-gray-500">
                      Removes all stored face embeddings. Use after a model switch or bad enrollment.
                  </p>
              </div>

              {/* Manual Inside/Outside Override */}
              <div>
                  <label className="block text-sm text-gray-300 mb-2">Location Status</label>
                  <div className="flex items-center gap-3">
                      <span className={`text-sm ${!editIsInside ? "text-white" : "text-gray-500"}`}>
                          Outside
                      </span>
                      <button
                          type="button"
                          onClick={() => handleIsInsideToggle(!editIsInside)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              editIsInside ? "bg-emerald-600" : "bg-gray-600"
                          }`}
                      >
                          <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                  editIsInside ? "translate-x-6" : "translate-x-1"
                              }`}
                          />
                      </button>
                      <span className={`text-sm ${editIsInside ? "text-white" : "text-gray-500"}`}>
                          Inside
                      </span>
                  </div>
              </div>
            </div>

            {/* Footer — always visible */}
            <div className="flex gap-3 px-6 py-4 border-t border-white/10">
              <Button
                className="flex-1 bg-gray-700 hover:bg-gray-600"
                onClick={() => setEditingPerson(null)}
                disabled={isUpdating}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-blue-600 hover:bg-blue-500"
                onClick={handleUpdate}
                disabled={isUpdating || !editName.trim()}
              >
                {isUpdating ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>

          {/* Biometric enrollment overlay — rendered inside the modal container so it
              stacks on top of the edit modal content. */}
          {!PILOT && showEditBiometric && (
            <BiometricEnrollment
                name={editName}
                storageKey={`enroll::edit::${editingPerson.id}::${Date.now()}`}
                residentId={editingPerson.id}
                onClose={() => setShowEditBiometric(false)}
            />
        )}
        </div>
      )}
    </div>
  );
}